import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import { environment } from '@fantaz/shared/app-config';
import {
  BeginMatchRequest,
  BeginMatchResponseSuccess,
  CasualMatchParameters,
  CasualTournamentDetails,
  GAEventKeys,
  PrizeDetails,
} from '@fantaz/shared/data-access/models';
import { Subject, takeUntil } from 'rxjs';
import { DevToolDetectionService } from './devtool-detection.service';
import { FzGoogleTagManagerService } from './fz-googleTag-manager.service';
import { WebsocketService } from './websocket.service';
import { GameConfig, GAMES_SETTINGS } from '@fantaz/shared/config';

@Injectable({
  providedIn: 'root',
})
export class MatchMakingService {
  count = 0;
  intentionalClose = true;
  beginMatch: CasualMatchParameters;
  processPercentComplete = 10;
  progressSteps = 20;
  tournamentData: CasualMatchParameters | null = null;
  currentTournamentUrl: string;
  requestBeginMatch: BeginMatchRequest;
  devToolIsOpen = false;
  isGTMBlocked = false;
  destroy$ = new Subject<void>();
  constructor(
    private websocketService: WebsocketService,
    private googleTagManagerService: FzGoogleTagManagerService,
    private platformLocation: PlatformLocation,
    private devToolDetectionService: DevToolDetectionService,
  ) {
    this.startDevtoolDetector();
    this.googleTagManagerService
      .isGTMBlocked()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isGTMBlocked) => {
        this.isGTMBlocked = isGTMBlocked;
      });
  }
  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  startDevtoolDetector() {
    this.devToolDetectionService.devToolIsOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => {
        if (isOpen !== this.devToolIsOpen) {
          this.devToolIsOpen = isOpen;
        }
      });
  }

  initMatchMaking(
    parameters: CasualMatchParameters,
    messageCallback: (message: any, progress?: number) => void,
    findMatchErrorCallback: () => void,
  ) {
    this.beginMatch = parameters;
    this.processPercentComplete = 10;
    this.tournamentData = parameters;
    this.progressSteps =
      parameters.openTournament.lifeExperience === true ? 25 : 20;
    const smartlookInfo = this.googleTagManagerService.getSmartlookInfo();
    const clarityBlocked = this.googleTagManagerService.getClarityInfo();
    console.log(`Tournament: ${parameters.openTournament?.eventPrototypeUuid}`);
    console.log(
      `Tournament: ${parameters.scheduleSelected?.eventScheduleUuid}`,
    );
    this.requestBeginMatch = {
      type: 'find-match',
      eventPrototypeUuid: parameters.openTournament?.eventPrototypeUuid,
      eventScheduleUuid: parameters.scheduleSelected?.eventScheduleUuid,
      eventCode: parameters.eventCode,
      gameType: 'CASUAL',
      nonce: 'ca88a6c9-efdd-40b5-a814-a77f8f280b88',
      action: 'findMatch',
      passUuid: parameters.pass?.uuid,
      userLocation: parameters.userLocation,
      devToolIsOpen: this.devToolIsOpen,
      monitoringSystems: {
        gtmBlocked: this.isGTMBlocked || false,
        smartlookBlocked: smartlookInfo?.recordingAllowed ? false : true,
        clarityBlocked: clarityBlocked ? false : true,
      },
    };
    if (parameters.requiredEventCode) {
      this.requestBeginMatch.requiredEventCode = parameters.requiredEventCode;
    }
    const tournamentType = parameters.openTournament?.lifeExperience
      ? 'prizes'
      : 'cash';
    this.currentTournamentUrl = `${tournamentType}/${parameters.openTournament.url}`;
    localStorage.setItem('current-tournament-url', this.currentTournamentUrl);
    localStorage.setItem(
      'current-tournament-uuid',
      parameters.openTournament?.eventPrototypeUuid,
    );
    localStorage.setItem(
      'current-schedule-uuid',
      parameters.scheduleSelected?.eventScheduleUuid,
    );
    localStorage.setItem(
      'current-open-tournament',
      JSON.stringify(parameters.openTournament),
    );
    const startLocationMessage = {
      subtype: 'Find Location',
      clientMessage: 'Capturing current location',
      complete: true,
      success: true,
    };
    messageCallback(startLocationMessage);
    this.websocketService.sendMessage(this.requestBeginMatch);
    this.websocketService.on()?.subscribe({
      next: (v) => {
        console.log(v);
        this.processMessageReceived(v, messageCallback, findMatchErrorCallback);
      },
      error: (e) => {
        console.error(e);
        findMatchErrorCallback();
      },
      complete: () => {
        console.log('complete');
      },
    });
  }

  getFreeGameUrl(
    tournament: CasualTournamentDetails | { name: string; game: any },
  ): string {
    const redirect = this.platformLocation.href.replace(
      environment.baseURL,
      '',
    );
    const obj = Object.assign({}, tournament, { isFree: true });
    localStorage.setItem('current-open-tournament', JSON.stringify(obj));
    const playInstanceUuid = 'playInstanceUuid';
    const transientPlay = 'true';
    const eventSeed = 0;
    const url = this.getGameUrl(
      tournament,
      playInstanceUuid,
      eventSeed,
      transientPlay,
      redirect,
    );
    this.destroy();
    return url.href;
  }

  closeSocket() {
    this.websocketService.close();
  }

  processMessageReceived(
    message: any,
    messageCallback: (message: any, progress?: number) => void,
    findMatchErrorCallback: () => void,
  ): void {
    if (message.type) {
      if (message.type === 'close') {
        this.count++;
        this.intentionalClose = true;
        if (message.success !== true) {
          if (this.count < 3 && message.needRetry === true) {
            this.websocketService.sendMessage(this.requestBeginMatch);
            this.processPercentComplete = 0;
          } else {
            messageCallback(message, this.processPercentComplete);
          }
        }
        if (this.count === 3) {
          this.closeSocket();
        }
      } else if (message.type === 'entry-fee-processed') {
        this.processPercentComplete += this.progressSteps;
        messageCallback(message, this.processPercentComplete);
      } else if (
        message.type === 'verification' ||
        message.type === 'match-found' ||
        message.type === 'validate-balance'
      ) {
        if (message.type === 'verification' && message.subtype === 'all') {
          if (this.devToolIsOpen) {
            message.success = false;
            message.complete = true;
            this.closeSocket();
          } else {
            this.processPercentComplete += this.progressSteps;
          }
        } else if (message.type === 'match-found') {
          if (!message.needRetry) {
            this.processPercentComplete += this.progressSteps;
          } else {
            if (this.beginMatch) {
              this.beginMatch.retry = true;
              this.beginMatch.playInstanceUuid = message.playInstanceUuid;
              this.initMatchMaking(
                this.beginMatch,
                messageCallback,
                findMatchErrorCallback,
              );
            }
          }
        }
        messageCallback(message, this.processPercentComplete);
      }
    }
  }

  public playCasualTournament(successResponse: any): void {
    const returnUrl = this.platformLocation.href.replace(
      environment.baseURL,
      '',
    );
    const gaEvt = this.sendGAEvent(successResponse.playInstanceUuid);
    if (gaEvt) {
      gaEvt
        .then(() => {
          this.redirectToPlay(successResponse, returnUrl);
        })
        .catch((err) => {
          console.error(err);
          this.redirectToPlay(successResponse, returnUrl);
        });
    } else {
      this.redirectToPlay(successResponse, returnUrl);
    }
  }

  redirectToPlay(
    successResponse: BeginMatchResponseSuccess,
    returnUrl: string,
  ) {
    const tournament = this.tournamentData?.openTournament;
    if (tournament) {
      const playInstanceUuid = successResponse.playInstanceUuid;
      localStorage.setItem(
        'current-event-instance-uuid',
        successResponse.eventInstanceUuid,
      );
      const url = this.getGameUrl(
        tournament,
        playInstanceUuid,
        successResponse.eventSeed,
        'false',
        returnUrl,
      );
      this.destroy();
      window.location.href = url.toString();
    }
  }

  loadGameSetting(gameKey: string): GameConfig | null {
    if (gameKey) {
      const gameName = gameKey.split('.')[0].replace('\\', '');
      if (GAMES_SETTINGS[gameName]) {
        return GAMES_SETTINGS[gameName];
      }
    }
    return null;
  }

  getGameUrl(
    tournament: CasualTournamentDetails | { name: any; game: any },
    playInstanceUuid: string,
    eventSeed: number,
    transientPlay: string,
    redirect: string,
  ) {
    let url = new URL('/game-wrapper', environment.baseURL);
    const gameKey = tournament.game?.key ?? '';
    const gameSettings = this.loadGameSetting(gameKey);
    if (gameSettings?.standalone === true) {
      const parseGameKey = gameKey.replace('.', '/');
      const baseURL = `${environment.gameFileHost}${parseGameKey}/standalone.html`;
      url = new URL(baseURL);
    }
    const searchParams = url.searchParams;
    searchParams.append('gameName', tournament.name);
    searchParams.append('eventSeed', eventSeed.toString());
    searchParams.append('gameKey', gameKey);
    searchParams.append('transient', transientPlay);
    searchParams.append('returnUrl', redirect);

    if (this.tournamentData) {
      const tournamentNameWithoutSpaces =
        this.tournamentData.openTournament.name
          .replace(/\s+/g, '-')
          .toLocaleLowerCase();
      searchParams.append('config', tournamentNameWithoutSpaces);
    }
    if (playInstanceUuid !== 'playInstanceUuid')
      searchParams.append('playInstanceUuid', playInstanceUuid);
    return url;
  }

  getTotalPrizeValue(prizeDetails: PrizeDetails[]): number {
    let totalPrize = 0;
    if (prizeDetails && prizeDetails.length > 0) {
      prizeDetails.forEach((prize: PrizeDetails) => {
        totalPrize += Number(prize.prizeValue);
      });
    }
    return totalPrize;
  }

  private sendGAEvent(playInstanceUuid: string): Promise<any> | null {
    if (
      this.tournamentData?.scheduleSelected &&
      this.tournamentData?.openTournament &&
      this.tournamentData?.user
    ) {
      const tournament_id =
        this.tournamentData.scheduleSelected.eventScheduleUuid;
      const tournament_type = this.tournamentData.openTournament.lifeExperience
        ? 'prize'
        : 'cash';
      const number_of_player =
        this.tournamentData.scheduleSelected.totalEntryCount;
      const total_prize = this.getTotalPrizeValue(
        this.tournamentData.scheduleSelected.prizeDetails,
      );
      const game = this.tournamentData.openTournament?.game?.name;
      const tournamentName = this.tournamentData.openTournament.name;
      const userId = this.tournamentData.user.uuid;
      const username = this.tournamentData.user.username;
      const dateTime = new Date().getTime();

      let passEvent = null;
      const event = {
        event: GAEventKeys.USER_JOINS_TOURNAMENT,
        timestamp: dateTime,
        tournament_id: tournament_id,
        type_of_tournament: tournament_type,
        total_prize: total_prize,
        game: game,
        tournament_name: tournamentName,
        user_id: userId,
        user_name: username,
        entry_amount: this.tournamentData.scheduleSelected.entryAmount,
        number_of_player: number_of_player,
        instance_id: playInstanceUuid,
      };
      localStorage.setItem('user_joins_tournament', JSON.stringify(event));

      if (this.beginMatch.pass) {
        passEvent = {
          event: GAEventKeys.USER_JOINS_TOURNAMENT_BY_PASS,
          timestamp: dateTime,
          tournament_id: tournament_id,
          type_of_tournament: tournament_type,
          total_prize: total_prize,
          game: game,
          tournament_name: tournamentName,
          user_id: userId,
          user_name: username,
          pass_id: this.beginMatch.pass.uuid,
          pass_name: this.beginMatch.pass.name,
          pass_price: this.beginMatch.pass.price.price,
          entry_amount: this.tournamentData.scheduleSelected.entryAmount,
          number_of_player: number_of_player,
          instance_id: playInstanceUuid,
        };

        const request = [
          this.googleTagManagerService.pushTag(passEvent),
          this.googleTagManagerService.pushTag(event),
        ];
        return Promise.all(request);
      } else {
        return this.googleTagManagerService.pushTag(event);
      }
    }
    return null;
  }
}
